import { createBrowserRouter, type DataStrategyFunction, type RouteObject } from "react-router";
import { markEverythingStale } from "@/lib/query";
import Root, { rootLoader, signOutAction } from "@/routes/root";
import Home, { homeLoader } from "@/routes/home";
import SignIn, { signInAction, signInLoader } from "@/routes/sign-in";
import SignUp, { signUpAction, signUpLoader } from "@/routes/sign-up";
import ChoosePassword, { choosePasswordAction, passwordLinkLoader } from "@/routes/choose-password";
import ForgotPassword, { forgotPasswordAction } from "@/routes/forgot-password";
import Welcome, { welcomeAction, welcomeLoader } from "@/routes/welcome";
import Spaces, {
  spacesAction,
  spacesLoader,
} from "@/routes/spaces";
import Space, { spaceAction, spaceLoader } from "@/routes/space";
import SpaceMembers, { membersLoader } from "@/routes/space-members";
import SpaceSearch, { spaceSearchLoader } from "@/routes/space-search";
import SpacesFrame from "@/routes/spaces-frame";
import SpaceRequests, { requestsLoader } from "@/routes/space-requests";
import SpaceInvite, { inviteLoader } from "@/routes/space-invite";
import SpaceSettings, { settingsLoader } from "@/routes/space-settings";
import { spaceSectionAction } from "@/routes/space-section";
import { leaveAction, leavingLoader } from "@/routes/space-leave";
import NewEvent, { newEventAction, newEventLoader } from "@/routes/event-new";
import EventPage, { eventAction, eventLoader } from "@/routes/event";
import EditEvent, { editEventAction, editEventLoader } from "@/routes/event-edit";
import BoardPage, { boardLoader } from "@/routes/board";
import VerifyPage, { verifyAction, verifyLoader } from "@/routes/event-verify";
import HiddenPage, { hiddenAction, hiddenLoader } from "@/routes/event-hidden";
import ResultPage, { resultAction, resultLoader } from "@/routes/result";
import TeamPage, { teamAction, teamLoader } from "@/routes/team";
import EntryView, { entryAction, entryLoader } from "@/routes/entry";
import NewWorkout, { newWorkoutAction, newWorkoutLoader } from "@/routes/workout-new";
import InvitationPage, { invitationAction, invitationLoader } from "@/routes/invitation";
import Account, { accountLoader } from "@/routes/account";
import { accountAction, sessionLoader } from "@/routes/account-section";
import AccountProfile from "@/routes/account-profile";
import AccountPassword from "@/routes/account-password";
import { AccountAppearance, AccountDivision, AccountTimeZone, defaultsLoader } from "@/routes/account-defaults";
import DeleteAccount, { deleteAccountLoader } from "@/routes/account-delete";
import FeedbackBoard, { feedbackAction, feedbackLoader } from "@/routes/feedback";
import NewFeedback, { newFeedbackAction, newFeedbackLoader } from "@/routes/feedback-new";
import FeedbackPage, { feedbackItemAction, feedbackItemLoader } from "@/routes/feedback-item";
import Inbox, { inboxAction, inboxLoader } from "@/routes/inbox";
import { conversationAction, conversationLoader } from "@/routes/conversation";
import { followAction } from "@/routes/follow";
import NotFound, { RootError } from "@/routes/not-found";

/**
 * Data mode: loaders fetch before a route renders, and actions handle form
 * submissions, so there is no request state to hold in components.
 *
 * The session is loaded once by the root loader and reused (see lib/session),
 * and a redirect from an action re-runs every loader — which is what makes the
 * page update after signing in or out.
 */
export const routes: RouteObject[] = [
  {
    id: "root",
    path: "/",
    Component: Root,
    loader: rootLoader,
    // Every navigation, not only after an action: the header's inbox count
    // is on the session, and it should not wait for a form to be sent to
    // move. The session is a query: drawn from its copy, read again behind
    // it, and asked once however many loaders want it.
    shouldRevalidate: () => true,
    // Only when the layout itself fails, the session unread: there is no
    // layout to draw inside.
    ErrorBoundary: RootError,
    children: [
      {
        // Any thrown response or error from a page lands here, inside the
        // layout — its margins, and the tab bar when signed in — rather
        // than in place of it: a space somebody cannot open is a 404 like
        // a mistyped address, and looks like one.
        ErrorBoundary: NotFound,
        children: [
          { index: true, Component: Home, loader: homeLoader },
          { path: "sign-in", Component: SignIn, loader: signInLoader, action: signInAction },
          { path: "sign-up", Component: SignUp, loader: signUpLoader, action: signUpAction },
          // The pages a sign-up or reset link opens: one page, the token in the
          // fragment. It reads the link from /password-link, which has a loader
          // and no page, because no loader is ever given the fragment.
          { path: "sign-up/finish", Component: ChoosePassword, action: choosePasswordAction },
          { path: "reset-password", Component: ChoosePassword, action: choosePasswordAction },
          { path: "password-link", loader: passwordLinkLoader },
          { path: "forgot-password", Component: ForgotPassword, action: forgotPasswordAction },
          // Spaces and searching for spaces (0085): one frame around both,
          // holding the search box, so the box a tap focuses on Spaces stays
          // focused on the search page — and a phone's keyboard stays up.
          // Asking to join and withdrawing post to Spaces' action from either.
          {
            path: "spaces",
            Component: SpacesFrame,
            children: [
              { index: true, Component: Spaces, loader: spacesLoader, action: spacesAction },
              { path: "search", Component: SpaceSearch, loader: spaceSearchLoader, action: spacesAction },
            ],
          },
          // The same /t/<slug>/ addressing the api uses, so a link to a
          // space is the same shape on both sides — /t/me, the caller's
          // own personal space, included. A space's page is its feed of
          // events; running it is one level down, a page for each part, all
          // sharing one action.
          // A public space somebody is not in shows them its presentation page
          // there, with Ask to join: the route's action.
          { path: "t/:slug", Component: Space, loader: spaceLoader, action: spaceAction },
          { path: "t/:slug/members", Component: SpaceMembers, loader: membersLoader, action: spaceSectionAction },
          { path: "t/:slug/requests", Component: SpaceRequests, loader: requestsLoader, action: spaceSectionAction },
          { path: "t/:slug/invite", Component: SpaceInvite, loader: inviteLoader, action: spaceSectionAction },
          { path: "t/:slug/settings", Component: SpaceSettings, loader: settingsLoader, action: spaceSectionAction },
          // No page of its own: the leave dialog's count, and leaving.
          { path: "t/:slug/leave", loader: leavingLoader, action: leaveAction },
          // No page of its own: what a thread reads past its first page, and
          // every write to one, reached by fetchers from the pages that show it.
          { path: "t/:slug/conversation", loader: conversationLoader, action: conversationAction },
          // No page of its own either: following an event, from its page and
          // from every card that shows it.
          { path: "t/:slug/events/:event/follow", action: followAction },
          // "new" is a static segment, so it ranks above :event and is never read
          // as an event's id.
          {
            path: "t/:slug/events/new",
            Component: NewEvent,
            loader: newEventLoader,
            action: newEventAction,
          },
          {
            path: "t/:slug/events/:event",
            Component: EventPage,
            loader: eventLoader,
            action: eventAction,
          },
          // A part's leaderboard, and the overall ranking, each a screen of its
          // own (10 October). Verifying and hiding from a line post to the event
          // page's action, which takes those intents.
          {
            path: "t/:slug/events/:event/parts/:part",
            Component: BoardPage,
            loader: boardLoader,
            action: eventAction,
          },
          {
            path: "t/:slug/events/:event/overall",
            Component: BoardPage,
            loader: boardLoader,
            action: eventAction,
          },
          {
            path: "t/:slug/events/:event/edit",
            Component: EditEvent,
            loader: editEventLoader,
            action: editEventAction,
          },
          // An event's verification queue, for the owner and verify_scores.
          {
            path: "t/:slug/events/:event/verify",
            Component: VerifyPage,
            loader: verifyLoader,
            action: verifyAction,
          },
          // An event's hidden results, for the owner and moderate (0026).
          {
            path: "t/:slug/events/:event/hidden",
            Component: HiddenPage,
            loader: hiddenLoader,
            action: hiddenAction,
          },
          // One athlete's shared result, with its own reactions and thread; a
          // board line's athlete links here.
          {
            path: "t/:slug/events/:event/results/:entry",
            Component: ResultPage,
            loader: resultLoader,
            // A moderator hiding its photo, or putting it back (7g).
            action: resultAction,
          },
          { path: "t/:slug/events/:event/teams/:team", Component: TeamPage, loader: teamLoader, action: teamAction },
          // Logging a workout of your own. "new" is static, so it ranks above
          // :entry, as "me" does above :slug.
          {
            path: "t/me/entries/new",
            Component: NewWorkout,
            loader: newWorkoutLoader,
            action: newWorkoutAction,
          },
          // One result in the log. Only ever the caller's own, so only under /t/me:
          // "me" is a static segment, so it ranks above :slug.
          {
            path: "t/me/entries/:entry",
            Component: EntryView,
            loader: entryLoader,
            action: entryAction,
          },
          // Where the api sends an account Google has just made, once (0076).
          { path: "welcome", Component: Welcome, loader: welcomeLoader, action: welcomeAction },
          // Account is a list; each row opens a page of its own, all posting
          // to the same action (0068).
          { path: "account", Component: Account, loader: accountLoader },
          { path: "account/profile", Component: AccountProfile, loader: sessionLoader, action: accountAction },
          { path: "account/password", Component: AccountPassword, loader: sessionLoader, action: accountAction },
          { path: "account/division", Component: AccountDivision, loader: defaultsLoader, action: accountAction },
          { path: "account/time-zone", Component: AccountTimeZone, loader: defaultsLoader, action: accountAction },
          { path: "account/appearance", Component: AccountAppearance, loader: sessionLoader },
          { path: "account/delete", Component: DeleteAccount, loader: deleteAccountLoader, action: accountAction },
          // The feedback board, reached from Account while a deployment takes
          // it (0091). Its action takes Me too, from the cards and the item's
          // page alike; "new" is static, so it ranks above :id.
          { path: "feedback", Component: FeedbackBoard, loader: feedbackLoader, action: feedbackAction },
          { path: "feedback/new", Component: NewFeedback, loader: newFeedbackLoader, action: newFeedbackAction },
          { path: "feedback/:id", Component: FeedbackPage, loader: feedbackItemLoader, action: feedbackItemAction },
          // Notifications across every space, invitations, and results waiting.
          { path: "inbox", Component: Inbox, loader: inboxLoader, action: inboxAction },
          // Where an emailed invitation lands. It renders a choice; nothing is
          // joined until somebody presses a button.
          {
            path: "invitations/:token",
            Component: InvitationPage,
            loader: invitationLoader,
            action: invitationAction,
          },
          // Action-only: the header posts here to sign out.
          { path: "sign-out", action: signOutAction },
          { path: "*", Component: NotFound },
        ],
      },
    ],
  },
];

/**
 * Every action may have changed what any page shows, wherever it is: once it
 * has run, every copy is marked stale, so no action names what it touched,
 * and the pages read after it wait for fresh data (0075). Whether it worked
 * or not — a refused write may still have changed something on its way.
 * Writes are made from actions, never from a component, so this sees them
 * all; tests build their router with it too.
 */
export const dataStrategy: DataStrategyFunction = async ({ request, matches }) => {
  try {
    return Object.fromEntries(
      await Promise.all(matches.map(async (match) => [match.route.id, await match.resolve()] as const)),
    );
  } finally {
    if (request.method !== "GET") markEverythingStale();
  }
};

export const router = createBrowserRouter(routes, { dataStrategy });
